if (!customElements.get('ugc-video-slider')) {
  customElements.define(
    'ugc-video-slider',
    class UgcVideoSlider extends HTMLElement {
      constructor() {
        super();
        this.swiper = null;
        this.swiperModule = null;
        this.sectionId = this.getAttribute('data-section-id');
        this.slidesDesktop = Number(this.getAttribute('data-slides-desktop')) || 4;
        this.slidesMobile = Number(this.getAttribute('data-slides-mobile')) || 1.4;
        this.gapDesktop = Number(this.getAttribute('data-gap-desktop')) || 16;
        this.gapMobile = Number(this.getAttribute('data-gap-mobile')) || 16;
      }

      connectedCallback() {
        this.setupVideos();

        if (window.Shopify?.designMode) {
          this.loadSwiper().then(() => this.initSwiper());
          return;
        }

        const observer = new IntersectionObserver(
          async (entries) => {
            if (entries[0].isIntersecting) {
              observer.disconnect();
              await this.loadSwiper();
              this.initSwiper();
            }
          },
          { rootMargin: '200px' }
        );
        observer.observe(this);
      }

      async loadSwiper() {
        if (!this.swiperModule) {
          this.swiperModule = await import('https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.mjs');
        }
        return this.swiperModule;
      }

      initSwiper() {
        if (this.swiper || !this.swiperEls.swiperContainer) return;

        const Swiper = this.swiperModule.default;
        const { Navigation, Pagination } = this.swiperModule;
        const { swiperContainer, prev, next, pagination } = this.swiperEls;

        this.swiper = new Swiper(swiperContainer, {
          modules: [Navigation, Pagination],
          slidesPerView: this.slidesMobile,
          spaceBetween: this.gapMobile,
          watchOverflow: true,
          speed: 350,
          grabCursor: true,
          resistanceRatio: 0.85,
          navigation: prev && next ? { nextEl: next, prevEl: prev } : false,
          pagination: pagination ? { el: pagination, clickable: true } : false,
          observer: true,
          observeParents: true,
          breakpoints: {
            750: {
              slidesPerView: this.slidesDesktop,
              spaceBetween: this.gapDesktop,
            },
          },
        });
      }

      disconnectedCallback() {
        this.swiper?.destroy(true, true);
        this.swiper = null;
      }

      get swiperEls() {
        return {
          swiperContainer: this.querySelector(`[id^="Slider-${this.sectionId}"]`),
          prev: this.querySelector(`[id^="Slider-prev-${this.sectionId}"]`),
          next: this.querySelector(`[id^="Slider-next-${this.sectionId}"]`),
          pagination: this.querySelector(`[id^="Slider-pagination-${this.sectionId}"]`),
        };
      }

      setupVideos() {
        this.warmObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              if (entry.isIntersecting) {
                if (video.preload !== 'auto') video.preload = 'auto';
              } else if (video.paused) {
                video.preload = 'none';
              }
            });
          },
          { rootMargin: '100px', threshold: 0.25 }
        );

        this.players = Array.from(this.querySelectorAll('.ck-ugc-video-slide-inner')).map((wrapper) => {
          const video = wrapper.querySelector('video');
          if (!video) return null;

          const playBtn = wrapper.querySelector('.ck-ugc-video-play');
          const muteBtn = wrapper.querySelector('.ck-ugc-video-mute');
          const duration = wrapper.querySelector('.ck-ugc-video-duration');

          this.warmObserver.observe(video);

          const formatTime = (seconds) => {
            if (!isFinite(seconds)) return '0:00';
            const m = Math.floor(seconds / 60);
            const s = Math.floor(seconds % 60)
              .toString()
              .padStart(2, '0');
            return `${m}:${s}`;
          };

          const updateDuration = () => {
            if (!duration) return;
            const remaining = video.duration - video.currentTime;
            duration.textContent = formatTime(remaining);
          };

          const setPlayingState = (isPlaying) => {
            wrapper.classList.toggle('is-playing', isPlaying);
          };

          const play = () => {
            this.pauseAllExcept(video);
            setPlayingState(true);
            video.preload = 'auto';
            video.play().catch(() => setPlayingState(false));
          };

          const pause = () => {
            setPlayingState(false);
            video.pause();
          };

          video.addEventListener('waiting', () => wrapper.classList.add('is-buffering'));
          video.addEventListener('playing', () => wrapper.classList.remove('is-buffering'));
          video.addEventListener('canplay', () => wrapper.classList.remove('is-buffering'));

          playBtn?.addEventListener('click', () => {
            if (video.paused) {
              play();
            } else {
              pause();
            }
          });

          muteBtn?.addEventListener('click', () => {
            video.muted = !video.muted;
            wrapper.classList.toggle('is-muted', video.muted);
          });

          video.addEventListener('play', () => setPlayingState(true));
          video.addEventListener('pause', () => setPlayingState(false));
          video.addEventListener('ended', () => setPlayingState(false));
          video.addEventListener('timeupdate', updateDuration);
          video.addEventListener('loadedmetadata', updateDuration);

          wrapper.classList.toggle('is-muted', video.muted);

          return { video, wrapper };
        }).filter(Boolean);
      }

      pauseAllExcept(currentVideo) {
        this.players.forEach(({ video, wrapper }) => {
          if (video !== currentVideo && !video.paused) {
            wrapper.classList.remove('is-playing');
            video.pause();
          }
        });
      }
    }
  );
}
